/* eslint-disable react-hooks/exhaustive-deps */
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { IUserData, logout as logoutCognito } from '../hooks/useAuth';
import { getUserSigned } from '../hooks/useUsers';
import {
  getTokenCache,
  removeRefreshTokenCache,
  removeTokenCache,
  setRefreshTokenCache,
  setTokenCache,
} from '../services/api';

interface ICognitoSignInResult {
  ChallengeParameters: any;
  AuthenticationResult: {
    AccessToken: string;
    ExpiresIn: number;
    TokenType: 'Bearer';
    RefreshToken: string;
    IdToken: string;
  };
}

interface UserContextType {
  user: IUserData | null;
  token: string | null;
  isLoggedIn: boolean;
  updateUserContextState: (
    userData: IUserData,
    cognitoResult?: ICognitoSignInResult,
  ) => void;
  logout: () => void;
  updateUser: () => void;
  getLoggedUser: () => Promise<boolean>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  const [user, setUser] = useState<IUserData>({} as IUserData);
  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);

  const updateUserContextState = (
    userData: IUserData,
    cognitoResult?: ICognitoSignInResult,
  ) => {
    setUser(userData);

    if (cognitoResult) {
      setToken(cognitoResult.AuthenticationResult.AccessToken);
      setTokenCache(cognitoResult.AuthenticationResult.AccessToken);
      setRefreshToken(cognitoResult.AuthenticationResult.RefreshToken);
      setRefreshTokenCache(cognitoResult.AuthenticationResult.RefreshToken);
    }

    setIsLoggedIn(true);
  };

  const getLoggedUser = async () => {
    try {
      const checkTokenCache = getTokenCache();

      if (checkTokenCache) {
        const user = await getUserSigned();
        if (user) {
          updateUserContextState(user);
          return true;
        }
      }
      return false;
    } catch (e) {
      setIsLoggedIn(false);
      return false;
    }
  };

  useEffect(() => {
    const checkTokenCache = getTokenCache();
    if (checkTokenCache && !user?.role) {
      getUserSigned()
        .then((result) => {
          updateUserContextState(result);
        })
        .catch((error) => {
          console.error('Erro ao obter detalhes do usuário:', error);
        });
    }
  }, [user]);

  const updateUser = () => {
    getUserSigned()
      .then((result) => {
        updateUserContextState(result);
      })
      .catch((error) => {
        console.error('Erro ao obter detalhes do usuário:', error);
      });
  };

  const logout = async () => {
    try {
      await logoutCognito(token || '');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    } finally {
      setUser({} as IUserData);
      setToken(null);
      setIsLoggedIn(false);
      removeRefreshTokenCache();
      removeTokenCache();
    }
  };

  const value = useMemo(() => {
    return {
      user,
      token,
      refreshToken,
      isLoggedIn,
      updateUserContextState,
      logout,
      updateUser,
      getLoggedUser,
    };
  }, [user, token, refreshToken, isLoggedIn, updateUser]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
